
import React, { useState, useEffect } from 'react';
import { useAuthSession } from '@/hooks/useAuthSession';
import { supabase } from '@/lib/supabase';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

export function CommissionTable() {
  const { session } = useAuthSession();
  const [commissions, setCommissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    if (!session?.user?.id) return;

    const fetchCommissions = async () => {
      setIsLoading(true);
      try {
        // Fetch commissions from the database where the affiliate_id matches a record for the current user
        const { data: affiliateData, error: affiliateError } = await supabase
          .from('affiliates')
          .select('id')
          .eq('user_id', session.user.id)
          .single();
          
        if (affiliateError) throw affiliateError;
        
        if (affiliateData?.id) {
          const { data, error } = await supabase
            .from('affiliate_commissions')
            .select(`
              id,
              amount,
              status,
              created_at,
              description,
              referral_id,
              referral:affiliate_referrals(user_id)
            `)
            .eq('affiliate_id', affiliateData.id)
            .order('created_at', { ascending: false });
            
          if (error) throw error;
          
          console.log('Fetched commissions:', data);
          setCommissions(data || []);
        }
      } catch (err) {
        console.error('Error fetching commissions:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCommissions();
  }, [session?.user?.id]);
  
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };
  
  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="text-center py-8 text-red-500">
        <p>Error loading commission data: {error}</p>
      </div>
    );
  }
  
  return (
    <div>
      {commissions.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-muted-foreground">No commission history yet.</p>
          <p className="text-sm mt-2">When your referrals subscribe, commissions will appear here.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Referral</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {commissions.map(commission => (
                <TableRow key={commission.id}>
                  <TableCell>{formatDate(commission.created_at)}</TableCell>
                  <TableCell>
                    {/* Show a masked email or ID for privacy */}
                    User {commission.referral_id?.substring(0, 8)}...
                  </TableCell>
                  <TableCell>₹{commission.amount}</TableCell>
                  <TableCell>
                    <Badge variant={commission.status === 'paid' ? 'success' : 'outline'}>
                      {commission.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
